import { TemplateTargetType } from '@compass/client/types/template';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Textarea } from '@components/ui/textarea';
import { normalizeEmail } from '@utils/validation';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateEmail, useUpdateEmail } from '@/hooks/useEmails';
import { useRenderTemplate, useTemplates } from '@/hooks/useTemplates';
import { useStore } from '@/store/store';
import type { ActionDataParams } from '@/types/actions';

interface SendEmailFormData {
  to: string;
  subject: string;
  body: string;
}

const CUSTOM_TEMPLATE_ID = 'custom';

interface ActionEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecordId: string;
  action: string;
  onActionSelect: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  title?: string;
  dialogDescription?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export function ActionEmailDialog({
  isOpen,
  onClose,
  caseRecordId,
  action,
  onActionSelect,
  title = 'Send Email',
  dialogDescription = 'Details for email',
  confirmButtonText = 'Send Email',
  cancelButtonText = 'Cancel',
}: ActionEmailDialogProps) {
  const createEmail = useCreateEmail();
  const updateEmail = useUpdateEmail();
  const renderTemplate = useRenderTemplate();
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Fetch email templates scoped to the user's program
  const { data: templatesData, isLoading: isLoadingTemplates } = useTemplates({
    programId: selectedProgramId ?? undefined,
    targetType: TemplateTargetType.TEMPLATE_TARGET_TYPE_EMAIL,
    enabled: isOpen,
  });

  const templates = templatesData?.templates ?? [];
  const hasTemplates = templates.length > 0;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(CUSTOM_TEMPLATE_ID);
  const [emailId, setEmailId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SendEmailFormData>({
    to: '',
    subject: '',
    body: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Track if we've already initiated the create request
  const createInitiatedRef = useRef(false);

  // Create email draft when dialog opens
  useEffect(() => {
    if (isOpen && !emailId && !createInitiatedRef.current && !createEmail.isPending) {
      createInitiatedRef.current = true;
      // TODO: Move from_address to backend - should be determined based on program configuration
      createEmail.mutate(
        {
          caseRecordId,
          data: {
            case_id: caseRecordId,
            from_address: 'noreply@mail.100ms.ai',
            to_addresses: [],
            subject: '',
            body: '',
          },
        },
        {
          onSuccess: data => {
            setEmailId(data.email?.id ?? null);
            setFormData({
              to: data.email?.to_addresses?.[0] ?? '',
              subject: data.email?.subject ?? '',
              body: data.email?.body ?? '',
            });
          },
          onError: error => {
            console.error('Failed to create email:', error);
            createInitiatedRef.current = false;
            onClose();
          },
        },
      );
    }
  }, [isOpen, emailId, caseRecordId, createEmail, onClose]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      createInitiatedRef.current = false;
      setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
      setErrorMessage(null);

      if (templateId === CUSTOM_TEMPLATE_ID) {
        // User chose custom - clear subject and body
        setFormData(prev => ({
          ...prev,
          subject: '',
          body: '',
        }));
        return;
      }

      // Render the template with case record context
      renderTemplate.mutate(
        { templateId, caseRecordId },
        {
          onSuccess: data => {
            const renderedSubject = data.rendered?.email?.subject ?? '';
            const renderedBody = data.rendered?.email?.body ?? '';
            setFormData(prev => ({
              ...prev,
              subject: renderedSubject,
              body: renderedBody,
            }));
          },
          onError: error => {
            console.error('Failed to render template:', error);
            setErrorMessage('Failed to load template. Please try again or use a custom template.');
          },
        },
      );
    },
    [caseRecordId, renderTemplate],
  );

  const handleConfirm = useCallback(() => {
    // Normalize and validate required fields
    const normalizedTo = normalizeEmail(formData.to);
    if (!normalizedTo || !formData.subject.trim() || !formData.body.trim() || !emailId) {
      return;
    }
    setErrorMessage(null);

    // Update email details first
    updateEmail.mutate(
      {
        caseRecordId,
        emailId,
        data: {
          to_addresses: [normalizedTo],
          subject: formData.subject,
          body: formData.body,
        },
      },
      {
        onSuccess: () => {
          // Reset state
          setFormData({ to: '', subject: '', body: '' });
          setEmailId(null);
          setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
          // Close dialog
          onClose();
          // Trigger action with email_id
          onActionSelect(action, caseRecordId, { email_id: emailId });
        },
        onError: error => {
          console.error('Failed to update email:', error);
          setErrorMessage('Failed to send email. Please try again.');
        },
      },
    );
  }, [formData, caseRecordId, emailId, action, onActionSelect, onClose, updateEmail]);

  const handleClose = useCallback(() => {
    // Reset state on cancel
    setFormData({
      to: '',
      subject: '',
      body: '',
    });
    setEmailId(null);
    setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback((field: keyof SendEmailFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const isFormValid =
    formData.to.trim() !== '' && formData.subject.trim() !== '' && formData.body.trim() !== '';

  const isSubmitting = createEmail.isPending || updateEmail.isPending;
  const isRenderingTemplate = renderTemplate.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Template Selection - Loading State */}
          {isLoadingTemplates && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">Template</label>
              <div className="flex h-10 items-center rounded-md border border-input bg-background px-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
              </div>
            </div>
          )}

          {/* Template Selection */}
          {!isLoadingTemplates && hasTemplates && (
            <div className="grid gap-2">
              <label htmlFor="template-select" className="text-sm font-medium">
                Template
              </label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
                disabled={isLoadingTemplates || isSubmitting}
              >
                <SelectTrigger id="template-select">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_TEMPLATE_ID}>
                    <span className="text-muted-foreground">Custom</span>
                  </SelectItem>
                  {templates
                    .filter(template => template.id)
                    .map(template => (
                      <SelectItem key={template.id} value={template.id!}>
                        {template.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Loading state when rendering template */}
          {isRenderingTemplate && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading template...</span>
            </div>
          )}

          {/* Form fields - shown when not loading template */}
          {!isRenderingTemplate && (
            <>
              {/* Recipient */}
              <div className="grid gap-2">
                <label htmlFor="email-to" className="text-sm font-medium">
                  Recipient
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  id="email-to"
                  type="email"
                  value={formData.to}
                  onChange={e => updateField('to', e.target.value)}
                  placeholder="Enter recipient email address"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              {/* Subject */}
              <div className="grid gap-2">
                <label htmlFor="email-subject" className="text-sm font-medium">
                  Subject
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  id="email-subject"
                  type="text"
                  value={formData.subject}
                  onChange={e => updateField('subject', e.target.value)}
                  placeholder="Enter email subject"
                  disabled={isSubmitting}
                />
              </div>

              {/* Body */}
              <div className="grid gap-2">
                <label htmlFor="email-body" className="text-sm font-medium">
                  Message
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Textarea
                  id="email-body"
                  value={formData.body}
                  onChange={e => updateField('body', e.target.value)}
                  placeholder="Enter email message"
                  className="min-h-[150px]"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </div>

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || isRenderingTemplate || !isFormValid}
          >
            {isSubmitting ? 'Processing...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
